import {
  AutocompleteValue,
  AutocompleteValueOrFreeSoloValueMapping
} from "@mui/material";
import { JsonMaster } from "common/interfaces/JsonMaster";
import { useReferenceStyles } from "hooks/referenceStyles";
import { useStructureForPricingHooks } from "hooks/structureForPricingHooks";
import { useCallback, useEffect } from "react";
import { invokeConvertValue } from "../JsonMasterAutocomplete/utils";

export const useStructureForPricingAutocompleteHooks = <
  T extends JsonMaster,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>(
  multiple: boolean | undefined,
  setFatal: ((error?: string | null | undefined) => void) | undefined,
  onOptionsLoad: ((options: JsonMaster[]) => void) | undefined
) => {
  const { jsonMasters, setJsonMasters } = useStructureForPricingHooks(setFatal);

  const referenceStyles = useReferenceStyles();

  const getOptionLabel = useCallback(
    (option: AutocompleteValueOrFreeSoloValueMapping<T, FreeSolo>) => {
      if (typeof option === "string") {
        return option as string;
      }

      return (option as T)?.name || "？";
    },
    []
  );

  const getOptionSelected = useCallback(
    (
      option: T,
      value: AutocompleteValueOrFreeSoloValueMapping<T, FreeSolo>
    ): boolean => {
      return typeof value === "string"
        ? option.name === value
        : option.id === value.id;
    },
    []
  );

  const convertValue = useCallback(
    (
      value:
        | AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>
        | string
        | string[]
        | undefined
    ) => invokeConvertValue(multiple, jsonMasters, value),
    [jsonMasters, multiple]
  );

  useEffect(() => {
    if (onOptionsLoad) {
      onOptionsLoad(jsonMasters);
    }
  }, [jsonMasters, onOptionsLoad]);

  return {
    jsonMasters,
    setJsonMasters,
    referenceStyles,
    getOptionLabel,
    getOptionSelected,
    convertValue
  } as const;
};
